import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { MenuList, MenuListDto } from '../../../../core/models/system-settings/menu-lists.model';
import { GroupMenuOption } from '../../../../core/models/system-settings/group-menus.model';
import { GroupSubMenuOption } from '../../../../core/models/system-settings/group-sub-menus.model';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperMenuListsService } from '../../../../core/mappers/system-settings/mapper-menu-lists.service';
import { ModalMenuListComponent } from '../../modals/system-settings/modal-menu-list/modal-menu-list.component';

@Component({
  selector: 'app-menu-lists',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalMenuListComponent],
  templateUrl: './menu-lists.component.html',
  styleUrl: './menu-lists.component.css'
})
export class MenuListsComponent implements OnInit {
  menuLists = signal<MenuList[]>([]);
  groupMenuOptions = signal<GroupMenuOption[]>([]);
  groupSubMenuOptions = signal<GroupSubMenuOption[]>([]);
  isLoading = signal<boolean>(false);
  isDashboardOpen = signal<boolean>(true);
  Math = Math;

  // Search and Filters
  searchText = signal<string>('');
  selectedGroupMenuId = signal<number | null>(null);
  selectedGroupSubId = signal<number | null>(null);

  isGroupMenuDropdownOpen = signal<boolean>(false);
  isGroupSubDropdownOpen = signal<boolean>(false);

  // Modal State
  isModalOpen = signal<boolean>(false);
  selectedItem = signal<MenuList | null>(null);

  // Dashboard Stats
  dashboardStats = signal<any[]>([]);

  // Pagination
  currentPage = signal<number>(1);
  pageSize = signal<number>(20);
  pageSizeOptions = [20, 50, 100];

  // Sorting
  sortColumn = signal<string>('number');
  sortDirection = signal<'asc' | 'desc'>('asc');

  // Computed signals for filtering and pagination (Resolving NG0600)
  filteredData = computed(() => {
    let filtered = this.menuLists();
    const search = this.searchText().trim().toLowerCase();
    const filterMenuId = this.selectedGroupMenuId();
    const filterSubId = this.selectedGroupSubId();

    if (filterMenuId !== null) {
      filtered = filtered.filter(m => m.groupMenuId === filterMenuId);
    }

    if (filterSubId !== null) {
      filtered = filtered.filter(m => m.groupSubId === filterSubId);
    }

    if (search) {
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(search) ||
        m.path.toLowerCase().includes(search) ||
        m.groupMenuName.toLowerCase().includes(search) ||
        (m.groupSubName && m.groupSubName.toLowerCase().includes(search)) ||
        m.id.toString() === search
      );
    }

    const col = this.sortColumn();
    const dir = this.sortDirection();
    if (col) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let valA = a[col];
        let valB = b[col];

        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA === null || valA === undefined) valA = '';
        if (valB === null || valB === undefined) valB = '';

        if (valA < valB) return dir === 'asc' ? -1 : 1;
        if (valA > valB) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  });

  totalItems = computed(() => this.filteredData().length);

  filteredAndPaginatedData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredData().slice(start, start + this.pageSize());
  });

  // ================= Normal Functions =================

  constructor(
    private apiService: ApiSystemSettingsService,
    private mapper: MapperMenuListsService,
    private toast: ToastService
  ) { }

  ngOnInit() {
    this.getGroupMenuOptions();
    this.getGroupSubMenuOptions();
    this.getMenuLists();
  }

  /**
   * Toggles statistics dashboard.
   */
  toggleDashboard(): void {
    this.isDashboardOpen.set(!this.isDashboardOpen());
  }

  /**
   * Refreshes search filter states and reloads.
   */
  onRefresh(): void {
    this.searchText.set('');
    this.selectedGroupMenuId.set(null);
    this.selectedGroupSubId.set(null);
    this.currentPage.set(1);
    this.getGroupSubMenuOptions(); // Reload all submenus options
    this.getMenuLists();
  }

  /**
   * Compiles the KPI stats for the top cards.
   */
  private calculateDashboardStats(): void {
    const list = this.menuLists();
    const total = list.length;
    const activeTotal = list.filter(m => m.status).length;
    const inactiveTotal = total - activeTotal;

    this.dashboardStats.set([
      { title: 'รายการเมนูย่อยทั้งหมด', value: total.toLocaleString(), icon: 'menu_book', color: 'text-amber-500' },
      { title: 'เปิดใช้งาน', value: activeTotal.toLocaleString(), icon: 'check_circle', color: 'text-emerald-500' },
      { title: 'ปิดใช้งาน', value: inactiveTotal.toLocaleString(), icon: 'cancel', color: 'text-rose-500' }
    ]);
  }



  /**
   * Triggered on search text submit.
   */
  public onSearch(): void {
    this.currentPage.set(1);
  }

  /**
   * Sorts data by selected column.
   * @param column Selected column name
   */
  public sortData(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
  }

  /**
   * Opens create modal.
   */
  public navigateToCreate(): void {
    this.selectedItem.set(null);
    this.isModalOpen.set(true);
  }

  /**
   * Opens edit modal.
   * @param item Selected menu item model
   */
  public navigateToEdit(item: MenuList): void {
    this.selectedItem.set(item);
    this.isModalOpen.set(true);
  }

  /**
   * Closes edit/create modal.
   */
  public closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedItem.set(null);
  }



  // --- Filter Dropdowns Toggle ---

  public toggleGroupMenuDropdown(): void {
    this.isGroupMenuDropdownOpen.set(!this.isGroupMenuDropdownOpen());
    if (this.isGroupMenuDropdownOpen()) this.isGroupSubDropdownOpen.set(false);
  }

  public toggleGroupSubDropdown(): void {
    this.isGroupSubDropdownOpen.set(!this.isGroupSubDropdownOpen());
    if (this.isGroupSubDropdownOpen()) this.isGroupMenuDropdownOpen.set(false);
  }

  /**
   * Selects group menu filter and cascade refreshes group sub menu filter options.
   * @param id Group Menu ID
   */
  public selectGroupMenu(id: number | null): void {
    this.selectedGroupMenuId.set(id);
    this.selectedGroupSubId.set(null); // Reset child filter
    this.isGroupMenuDropdownOpen.set(false);

    // Cascade filter sub menu options based on group ID
    this.getGroupSubMenuOptions(id || undefined);
    this.onSearch();
  }

  public selectGroupSub(id: number | null): void {
    this.selectedGroupSubId.set(id);
    this.isGroupSubDropdownOpen.set(false);
    this.onSearch();
  }

  get selectedGroupMenuName(): string {
    const id = this.selectedGroupMenuId();
    if (id === null) return 'ทุกกลุ่มเมนู';
    const match = this.groupMenuOptions().find(o => o.id === id);
    return match ? match.name : 'ทุกกลุ่มเมนู';
  }

  get selectedGroupSubName(): string {
    const id = this.selectedGroupSubId();
    if (id === null) return 'ทุกกลุ่มเมนูย่อย';
    const match = this.groupSubMenuOptions().find(o => o.id === id);
    return match ? match.name : 'ทุกกลุ่มเมนูย่อย';
  }

  // --- Pagination Helpers ---

  get totalPages(): number {
    return Math.ceil(this.totalItems() / (this.pageSize() || 1));
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
    }
  }

  public onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
  }

  public trackById(index: number, item: MenuList): number {
    return item.id;
  }

  // ================= API Functions =================

  /**
   * Loads options for parent select dropdown.
   */
  getGroupMenuOptions(): void {
    this.apiService.getGroupMenuOptions().subscribe({
      next: (options: GroupMenuOption[]) => {
        this.groupMenuOptions.set(options || []);
      },
      error: () => {
        const fallbackOptions = [
          { id: 1, name: 'HOME' },
          { id: 2, name: 'PARKING SYSTEM' },
          { id: 3, name: 'SETTINGS' },
          { id: 4, name: 'อื่นๆ' }
        ];
        this.groupMenuOptions.set(fallbackOptions);
      }
    });
  }

  /**
   * Loads options for sub-parent select dropdown, optionally filtered.
   * @param groupMenuId Optional parent group ID filter
   */
  getGroupSubMenuOptions(groupMenuId?: number): void {
    this.apiService.getGroupSubMenuOptions(groupMenuId).subscribe({
      next: (options: GroupSubMenuOption[]) => {
        this.groupSubMenuOptions.set(options || []);
      },
      error: () => {
        const fallbackOptions = [
          { id: 101, name: 'จัดการข้อมูลลูกค้า' },
          { id: 102, name: 'จัดการที่จอดรถ' },
          { id: 103, name: 'การเงิน' },
          { id: 104, name: 'จัดการเมนู' }
        ];
        this.groupSubMenuOptions.set(fallbackOptions);
      }
    });
  }

  /**
   * Fetches menu items list and maps to logic model.
   */
  getMenuLists(): void {
    this.isLoading.set(true);
    this.apiService.getMenuListsForTable().subscribe({
      next: (dtos: MenuListDto[]) => {
        console.log("Get munu List : ", dtos)
        try {
          const list = (dtos || []).map((d: MenuListDto) => this.mapper.mapMenuListFromDto(d));
          this.menuLists.set(list);
          this.calculateDashboardStats();
        } catch (error) {
          this.toast.error('โครงสร้างข้อมูลไม่ถูกต้อง');
        } finally {
          this.isLoading.set(false);
        }
      },
      error: (err: any) => {
        console.error(err);
        this.menuLists.set([]);
        this.calculateDashboardStats();
        this.isLoading.set(false);
      }
    });
  }
}
